import { Box, FormControlLabel, FormHelperText, MenuItem, Radio, styled } from '@mui/material';
import UploadField from 'FeatureModules/DataImport/Components/Forms/UploadField';
import { ThemeTypography } from 'DesignSystem';
import { ErrorMessage, Field, useField, useFormikContext } from 'formik';
import { Checkbox, RadioGroup, Select, TextField } from 'formik-mui';
import React, { FC } from 'react';
import {
  EQuestionType,
  IQuestionnaireQuestion,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import FlexBox from 'Components/FlexBox';

// Custom wrapper for multi-select to ensure value is always an array
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SafeMultiSelect: FC<any> = props => {
  // Known issue in the formik library that pressing escape key produces a string instead of array and an error is thrown: `UI: The value prop must be an array when using the Select component with multiple.`
  // Do not select anything if pressing escape key
  return <Select {...props} onClose={() => undefined} />;
};

interface IQuestionProps {
  name: string;
  sectionIndex: number;
  questionIndex: number;
  question: IQuestionnaireQuestion;
}

const QuestionContainer = styled(FlexBox)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(2),
  borderRadius: 6,
  background: theme.custom.themeColors.white,
}));

/** Remove automatically added margin from first paragraph */
const Description = styled(ThemeTypography)(({ theme }) => ({
  '& p:first-child': {
    marginTop: 0,
  },
}));

const Question: FC<IQuestionProps> = ({
  name,
  sectionIndex,
  questionIndex,
  question: { questionText, questionType, isRequired, description, options },
}) => {
  /**
   * If any options requires a comment we show the comment on the question
   */
  const questionHasComment = options?.some(option => option?.isCommentRequired);

  const { isSubmitting, status } = useFormikContext();
  const [, { touched, error }] = useField(name);

  const highlightError = touched && error;
  const disabled = isSubmitting || !!status?.disabled;

  return (
    // Formik name as id is used to located the container is the DOM for scrolling to errors
    <QuestionContainer id={name}>
      <FlexBox>
        <ThemeTypography variant="BODY_LARGER_BOLD" color={highlightError ? 'RED' : 'BLACK'}>
          {sectionIndex + 1}.{questionIndex + 1}. {questionText}
          {isRequired ? ' *' : ''}
        </ThemeTypography>
      </FlexBox>

      {description && (
        <Description variant="BODY_MEDIUM" dangerouslySetInnerHTML={{ __html: description }} />
      )}

      {/* Text fields */}
      {(questionType === EQuestionType.SHORT_FREE_TEXT ||
        questionType === EQuestionType.LONG_FREE_TEXT) && (
        <Field
          fullWidth
          disabled={disabled}
          component={TextField}
          label="Enter text"
          name={`${name}.answerText`}
          variant="outlined"
          {...(questionType === EQuestionType.LONG_FREE_TEXT
            ? { multiline: true, maxRows: 5, minRows: 3 }
            : {})}
        />
      )}

      {/* Single select radio buttons */}
      {questionType === EQuestionType.SINGLE_SELECT && (
        <Field
          fullWidth
          component={RadioGroup}
          label="Select an option"
          name={`${name}.selectedOptionId`}
          variant="outlined"
        >
          {options?.map(option => (
            <FormControlLabel
              disabled={disabled}
              key={option?.id}
              value={option?.id}
              control={<Radio />}
              label={option?.text}
            />
          ))}
          <ErrorMessage name={`${name}.selectedOptionId`}>
            {message => <FormHelperText error>{message}</FormHelperText>}
          </ErrorMessage>
        </Field>
      )}

      {/* Multi select checkboxes */}
      {questionType === EQuestionType.MULTI_SELECT && (
        <FlexBox flexDirection="column" alignItems="flex-start" gap={1}>
          {options?.map(option => (
            <FormControlLabel
              key={option?.id}
              control={
                <Field
                  disabled={disabled}
                  component={Checkbox}
                  name={`${name}.selectedOptionIds`}
                  value={option?.id}
                  type="checkbox"
                />
              }
              label={option?.text}
            />
          ))}
          <ErrorMessage name={`${name}.selectedOptionIds`}>
            {message => <FormHelperText error>{message}</FormHelperText>}
          </ErrorMessage>
        </FlexBox>
      )}

      {/* Single select dropdown */}
      {questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN && (
        <Field
          component={Select}
          disabled={disabled}
          label="Select an option"
          name={`${name}.selectedOptionId`}
          variant="outlined"
          formControl={{ fullWidth: true }}
        >
          {options?.map(option => (
            <MenuItem key={option?.id} value={option?.id}>
              {option?.text}
            </MenuItem>
          ))}
        </Field>
      )}

      {/* Multi select field */}
      {questionType === EQuestionType.MULTI_SELECT_DROP_DOWN && (
        <Field
          component={SafeMultiSelect}
          multiple
          disabled={disabled}
          label="Select options"
          name={`${name}.selectedOptionIds`}
          variant="outlined"
          formControl={{ fullWidth: true }}
        >
          {options?.map(option => (
            <MenuItem key={option?.id} value={option?.id}>
              {option?.text}
            </MenuItem>
          ))}
        </Field>
      )}

      {/* Document upload field */}
      {questionType === EQuestionType.DOCUMENTS && (
        <Field
          component={UploadField}
          disabled={disabled}
          disableErrorMessage
          name={`${name}.documents`}
          multiple={true}
          supportedFileTypes={['pdf', 'image']}
        />
      )}

      {questionHasComment && (
        <Box mb={3} width="100%">
          <Field
            fullWidth
            disabled={disabled}
            component={TextField}
            multiline
            maxRows={5}
            minRows={3}
            label="Additional comments"
            name={`${name}.commentText`}
            variant="outlined"
          />
        </Box>
      )}
    </QuestionContainer>
  );
};

export default Question;
